import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, PhoneCall, Users as UsersIcon, Mail, MessageSquare, FileText, Briefcase, BellRing, StickyNote, User, Contact as ContactIcon, CircleDot } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Section, Empty, fmtD, ACT_TYPE_LABELS } from "./teamShared";
import { translateFr as tfr } from "@/i18n";

const TYPE_ICONS = {
  call: PhoneCall, meeting: UsersIcon, email: Mail, message: MessageSquare, note: StickyNote,
  follow_up: BellRing, document: FileText, opportunity: Briefcase, athlete: User, contact: ContactIcon, other: CircleDot,
};

export default function TeamActivities({ cid }) {
  const [data, setData] = useState(null);
  const [typeF, setTypeF] = useState("");
  const [q, setQ] = useState("");
  const [offset, setOffset] = useState(0);
  const [composerOpen, setComposerOpen] = useState(false);

  const load = useCallback((reset = true) => {
    const off = reset ? 0 : offset + 50;
    api.get(`/clubs/${cid}/activities`, { params: { type: typeF || undefined, q: q || undefined, limit: 50, offset: off } })
      .then((r) => {
        setData((d) => reset ? r.data : { ...r.data, activities: [...(d?.activities || []), ...r.data.activities] });
        setOffset(off);
      }).catch(() => setData({ activities: [], total: 0, upcoming_events: [] }));
  }, [cid, typeF, q, offset]);

  useEffect(() => { load(true); }, [cid, typeF]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!data) return <div className="h-48 rounded-2xl bg-muted/60 animate-pulse" />;
  const acts = data.activities || [];
  const groups = acts.reduce((m, a) => {
    const day = fmtD(a.occurred_at);
    (m[day] = m[day] || []).push(a);
    return m;
  }, {});

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-4">
      <div className="space-y-4">
        <div className="flex flex-wrap gap-2 items-center">
          <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load(true)}
            placeholder={tfr("Rechercher une activité…")} className="h-10 max-w-xs" data-testid="act-search" />
          <div className="flex gap-1.5 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            {["", "call", "meeting", "email", "follow_up", "opportunity", "document"].map((t) => (
              <button key={t || "all"} onClick={() => setTypeF(t)} data-testid={`act-filter-${t || "all"}`}
                className={cn("h-9 px-3 rounded-full border text-xs whitespace-nowrap shrink-0 transition-colors",
                  typeF === t ? "border-athlynx/40 bg-athlynx/10 text-athlynx font-medium" : "border-border text-muted-foreground hover:text-foreground")}>
                {t ? ACT_TYPE_LABELS[t] : tfr("Toutes")}
              </button>
            ))}
          </div>
          <Button size="sm" onClick={() => setComposerOpen(true)} className="h-9 gap-1 bg-athlynx text-white hover:bg-athlynx/90 ml-auto" data-testid="act-add">
            <Plus className="h-3.5 w-3.5" /> Ajouter une activité
          </Button>
        </div>

        {acts.length === 0 ? (
          <Empty text={tfr("Les interactions significatives avec cette équipe apparaîtront ici.")} cta={tfr("Ajouter une activité")} onClick={() => setComposerOpen(true)} testid="act-empty" />
        ) : (
          <div className="space-y-4" data-testid="act-timeline">
            {Object.entries(groups).map(([day, items]) => (
              <div key={day}>
                <p className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1.5">{day}</p>
                <div className="rounded-2xl border border-border bg-card shadow-sm dark:shadow-none divide-y divide-border/70">
                  {items.map((a) => {
                    const Icon = TYPE_ICONS[a.type] || CircleDot;
                    return (
                      <div key={a.id} className="flex items-start gap-3 p-3" data-testid={`act-item-${a.id}`}>
                        <span className="h-8 w-8 rounded-full bg-secondary flex items-center justify-center shrink-0">
                          <Icon className="h-3.5 w-3.5 text-muted-foreground" aria-hidden />
                        </span>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium">{a.title}</p>
                          {a.note && <p className="text-xs text-muted-foreground mt-0.5 whitespace-pre-wrap">{a.note}</p>}
                          <p className="text-[10px] text-muted-foreground/70 mt-0.5">{ACT_TYPE_LABELS[a.type] || a.type}{a.auto ? ` · ${tfr("automatique")}` : ""}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
            {acts.length < data.total && (
              <Button variant="outline" size="sm" onClick={() => load(false)} className="w-full" data-testid="act-load-more">
                Charger plus ({data.total - acts.length} restantes)
              </Button>
            )}
          </div>
        )}
      </div>

      <Section title={tfr("À venir")} testid="act-upcoming" className="h-fit">
        {(data.upcoming_events || []).length ? data.upcoming_events.map((e) => (
          <div key={e.id} className="flex items-center justify-between py-1.5 border-b border-border/60 last:border-0 text-xs gap-2">
            <span className="font-medium truncate">{e.title}</span>
            <span className="text-muted-foreground tabular-nums shrink-0">{fmtD(e.starts_at)}</span>
          </div>
        )) : <p className="text-xs text-muted-foreground">{tfr("Aucune activité planifiée.")}</p>}
      </Section>

      {composerOpen && <ActivityComposer cid={cid} onClose={() => setComposerOpen(false)} onSaved={() => { setComposerOpen(false); load(true); }} />}
    </div>
  );
}

function ActivityComposer({ cid, onClose, onSaved }) {
  const [form, setForm] = useState({ type: "call", title: "", note: "", occurred_at: new Date().toISOString().slice(0, 10), contact_id: "", player_id: "" });
  const [contacts, setContacts] = useState([]);
  const [athletes, setAthletes] = useState([]);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    api.get(`/clubs/${cid}/contacts`).then((r) => setContacts(r.data || []));
    api.get(`/clubs/${cid}/athletes`).then((r) => setAthletes(r.data || []));
  }, [cid]);

  const submit = async () => {
    if (!form.title.trim() || busy) return;
    setBusy(true);
    try {
      await api.post(`/clubs/${cid}/activities`, {
        type: form.type, title: form.title, note: form.note,
        occurred_at: `${form.occurred_at}T12:00:00+00:00`,
        contact_id: form.contact_id || null, player_id: form.player_id || null,
      });
      toast.success(tfr("Activité enregistrée"));
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Enregistrement impossible"); } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="act-composer">
        <DialogHeader><DialogTitle className="text-base">{tfr("Ajouter une activité")}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div><Label className="text-xs">Type</Label>
            <Select value={form.type} onValueChange={(v) => set("type", v)}>
              <SelectTrigger className="h-10 mt-1 text-xs" data-testid="act-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                {["call", "meeting", "email", "message", "note", "follow_up", "other"].map((t) => (
                  <SelectItem key={t} value={t}>{ACT_TYPE_LABELS[t]}</SelectItem>
                ))}
              </SelectContent>
            </Select></div>
          <div><Label className="text-xs">Date</Label>
            <Input type="date" value={form.occurred_at} onChange={(e) => set("occurred_at", e.target.value)} className="h-10 mt-1" /></div>
          <div className="col-span-2"><Label className="text-xs">Titre</Label>
            <Input value={form.title} onChange={(e) => set("title", e.target.value)} placeholder={tfr("Ex. Appel avec le directeur sportif")} className="h-10 mt-1" data-testid="act-title" /></div>
          <div><Label className="text-xs">{tfr("Contact lié")}</Label>
            <Select value={form.contact_id || "none"} onValueChange={(v) => set("contact_id", v === "none" ? "" : v)}>
              <SelectTrigger className="h-10 mt-1 text-xs"><SelectValue placeholder={tfr("Aucun")} /></SelectTrigger>
              <SelectContent><SelectItem value="none">{tfr("Aucun")}</SelectItem>
                {contacts.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
            </Select></div>
          <div><Label className="text-xs">{tfr("Athlète lié")}</Label>
            <Select value={form.player_id || "none"} onValueChange={(v) => set("player_id", v === "none" ? "" : v)}>
              <SelectTrigger className="h-10 mt-1 text-xs"><SelectValue placeholder={tfr("Aucun")} /></SelectTrigger>
              <SelectContent><SelectItem value="none">{tfr("Aucun")}</SelectItem>
                {athletes.map((l) => <SelectItem key={l.player_id} value={l.player_id}>{l.player?.name}</SelectItem>)}</SelectContent>
            </Select></div>
          <div className="col-span-2"><Label className="text-xs">Notes</Label>
            <Textarea rows={2} value={form.note} onChange={(e) => set("note", e.target.value)} className="mt-1 resize-none" /></div>
        </div>
        <Button onClick={submit} disabled={!form.title.trim() || busy} className="bg-athlynx text-white hover:bg-athlynx/90" data-testid="act-submit">
          {tfr("Enregistrer l'activité")}
        </Button>
      </DialogContent>
    </Dialog>
  );
}
